import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { LogoMark } from "./Logo";
import { useT } from "../lib/i18n";
import { ForgotForm } from "./ForgotForm";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4]">{label}</span>
    <input {...props} className="mt-1.5 w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:outline-none focus:border-[#1FD1FF]" />
  </label>
);

export const AuthDialog = () => {
  const { authOpen, setAuthOpen, authMode, setAuthMode, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signup = authMode === "signup";
  const forgot = authMode === "forgot";
  const { t } = useT();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(signup ? "/auth/register" : "/auth/login", signup ? form : { email: form.email, password: form.password });
      setUser(data);
      setAuthOpen(false);
      toast.success(`${t(signup ? "auth.welcomeNew" : "auth.welcomeBack")} ${data.name}`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent data-testid="auth-dialog" className="bg-[#07101E] border-white/10 rounded-none max-w-md">
        <DialogHeader>
          <LogoMark className="w-10 h-10 mb-2" />
          <DialogTitle className="font-display text-5xl font-normal leading-none">{t(forgot ? "forgot.title" : signup ? "auth.joinTitle" : "auth.loginTitle")}</DialogTitle>
          {!forgot && <DialogDescription className="text-[#A8B3C4]">{t(signup ? "auth.joinDesc" : "auth.loginDesc")}</DialogDescription>}
        </DialogHeader>
        {forgot ? <ForgotForm onBack={() => setAuthMode("login")} /> : (
        <>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {signup && <Field label={t("auth.name")} data-testid="auth-name-input" value={form.name} onChange={set("name")} required />}
          <Field label={t("auth.email")} type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} required />
          <Field label={t("auth.password")} type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} required minLength={6} />
          {!signup && (
            <button type="button" data-testid="auth-forgot-link" onClick={() => { setError(""); setAuthMode("forgot"); }} className="font-mono text-xs text-[#1FD1FF] hover:text-white transition-colors">{t("auth.forgot")}</button>
          )}
          {error && <p data-testid="auth-error" className="text-sm text-[#FCA5A5]">{error}</p>}
          <button data-testid="fan-login-form-submit" disabled={busy} className="w-full py-3.5 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60">
            {busy ? t("auth.wait") : t(signup ? "auth.create" : "auth.login")}
          </button>
        </form>
        <button data-testid="auth-toggle-mode" onClick={() => { setError(""); setAuthMode(signup ? "login" : "signup"); }} className="font-mono text-xs text-[#A8B3C4] hover:text-[#1FD1FF] transition-colors">
          {t(signup ? "auth.toLogin" : "auth.toSignup")}
        </button>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
